"use client";

import { cn } from "@/lib/utils";
import { ProductInterface } from "@/types/product";
import { ImageLoader } from "../image-loader";
import Link from "next/link";
import { Star, Gift, ShoppingCart, Zap, Heart } from "lucide-react";
import { FormatUtils } from "@/utils/format";
import { BadgeFlashSale } from "../Badge/flashsale";
import { discountTypeEnum, PromotionInterface } from "@/types/promotion";
import { useAddToCart } from "@/hooks/use-addToCart";
import { useEffect, useState } from "react";

/* =========================================================
   GIFT ITEM
========================================================= */
export const RenderGiftItems = ({
  gift,
  className,
}: {
  gift: ProductInterface;
  className?: string;
}) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      window.location.href = `/san-pham/${gift.slug}`;
    }}
    className={cn(
      "relative group/gift cursor-pointer overflow-hidden rounded-lg border",
      "transition-all duration-200 hover:scale-105",
      className
    )}
    style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
  >
    <ImageLoader
      alt={gift.name}
      src={gift.images[0].url}
      fill
      className="object-cover"
    />
    <div
      className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
      style={{ backgroundColor: "var(--brand-primary)" }}
    >
      <Gift className="w-2 h-2" style={{ color: "var(--brand-primary-foreground)" }} />
    </div>
  </div>
);

/* =========================================================
   PRODUCT CARD
========================================================= */
interface ProductCardProps {
  product: ProductInterface;
  promotion?: PromotionInterface;
  isSingleColumn?: boolean;
  isPriority?: boolean;
}

export const ProductCard = ({
  product,
  promotion,
  isSingleColumn = false,
  isPriority = false,
}: ProductCardProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isWished, setIsWished]   = useState(false);
  const { addToCart }             = useAddToCart();

  const promotionProduct = product.promotionProducts;
  const hasPromotion     = promotionProduct.length > 0 && promotion;
  const hasGifts         = product.giftProducts!.length > 0;
  const isOutOfStock     = product.stock <= 0;
  const isLowStock       = !isOutOfStock && product.stock < 10;

  const getDiscountedPrice = () => {
    const promo = promotionProduct[0];
    if (!promo) return product.price;
    return promo.discountType === discountTypeEnum.PERCENT
      ? product.price * (1 - promo.discount / 100)
      : product.price - promo.discount;
  };
  const discountedPrice = getDiscountedPrice();

  const showLineThroughPrice = promotion
    ? product.price
    : product.originalPrice && product.originalPrice > product.price
    ? product.originalPrice
    : null;

  const discountPercent = showLineThroughPrice
    ? Math.round(((showLineThroughPrice - discountedPrice) / showLineThroughPrice) * 100)
    : 0;

  const handleAddToCart = async (e: React.MouseEvent, isCheckout: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart({ product, isCheckout });
  };

  useEffect(() => { setIsMounted(true); }, []);
  if (!isMounted) return null;

  return (
    <Link href={`/san-pham/${product.slug}`} className="block h-full" prefetch>
      <article
        className="group relative h-full flex flex-col rounded-2xl overflow-hidden border transition-all duration-300"
        style={{
          backgroundColor: "var(--card)",
          borderColor: "var(--border)",
          fontFamily: "var(--font-roboto)",
          willChange: "transform",
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = "rgba(200,169,110,0.5)";
          el.style.transform   = "translateY(-3px)";
          el.style.boxShadow   = "0 20px 48px rgba(0,0,0,0.28), 0 0 0 1px rgba(200,169,110,0.08)";
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = "var(--border)";
          el.style.transform   = "translateY(0)";
          el.style.boxShadow   = "none";
        }}
      >

        {/* ════════════════ IMAGE ════════════════ */}
        <div
          className="relative flex-shrink-0 overflow-hidden"
          style={{ aspectRatio: "1/1", backgroundColor: "var(--background)" }}
        >
          <ImageLoader
            src={product.images[0].url}
            alt={product.name}
            priority={isPriority}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-[1.07]"
          />

          {/* Hover gradient */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
              background: "linear-gradient(to top, rgba(0,0,0,0.42) 0%, transparent 55%)",
            }}
          />

          {/* ── Badges: top-left ── */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
            {hasPromotion && (
              <BadgeFlashSale promotion={promotion} className="text-xs border-0 shadow-lg" />
            )}
            {!hasPromotion && discountPercent > 0 && (
              <span
                className="px-2 py-0.5 rounded-md text-xs font-black shadow-md"
                style={{
                  backgroundColor: "#fb2150",
                  color: "#fff",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                -{discountPercent}%
              </span>
            )}
            {isLowStock && (
              <span
                className="px-2 py-0.5 rounded-md text-xs font-bold shadow-md"
                style={{
                  backgroundColor: "rgba(245,158,11,0.92)",
                  color: "#000",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                Còn {product.stock}
              </span>
            )}
          </div>

          {/* ── Wishlist: top-right ── */}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsWished(w => !w); }}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-90"
            style={{
              backgroundColor: "rgba(13,13,13,0.68)",
              borderColor: "rgba(255,255,255,0.14)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Heart
              className="w-3.5 h-3.5 transition-all duration-150"
              style={{
                fill:  isWished ? "#f43f5e" : "transparent",
                color: isWished ? "#f43f5e" : "rgba(255,255,255,0.85)",
              }}
            />
          </button>

          {/* ── Gift label: bottom-left ── */}
          {hasGifts && (
            <div
              className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0"
              style={{
                backgroundColor: "rgba(13,13,13,0.75)",
                borderColor: "rgba(200,169,110,0.5)",
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Gift className="w-3 h-3" />
              Có quà tặng
            </div>
          )}

          {/* ── Out of stock overlay ── */}
          {isOutOfStock && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center"
              style={{ backgroundColor: "rgba(0,0,0,0.62)" }}
            >
              <span
                className="px-4 py-1.5 rounded-full text-xs font-bold border"
                style={{
                  backgroundColor: "rgba(13,13,13,0.9)",
                  borderColor: "rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.55)",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                Hết hàng
              </span>
            </div>
          )}
        </div>

        {/* ════════════════ CONTENT ════════════════ */}
        <div className="flex flex-col flex-1 p-4 gap-2.5">

          {/* Rating row */}
          <div className="flex items-center gap-1.5">
            <div className="flex gap-px">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-3 h-3 fill-current"
                  style={{ color: "var(--brand-primary)" }}
                />
              ))}
            </div>
            <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>5.0</span>
          </div>

          {/* Name */}
          <h3
            className="text-sm font-bold leading-snug line-clamp-2 flex-shrink-0"
            style={{
              fontFamily: "var(--font-montserrat)",
              color: "var(--foreground)",
              minHeight: "2.5rem",
            }}
          >
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-base font-black leading-none"
              style={{
                fontFamily: "var(--font-montserrat)",
                color: discountedPrice === 0 ? "var(--muted-foreground)" : "var(--brand-primary)",
              }}
            >
              {discountedPrice === 0
                ? "Liên hệ"
                : FormatUtils.formatPriceVND(discountedPrice)}
            </span>
            {showLineThroughPrice && (
              <span
                className="text-xs line-through"
                style={{ color: "var(--muted-foreground)" }}
              >
                {FormatUtils.formatPriceVND(showLineThroughPrice)}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="h-px w-full" style={{ backgroundColor: "var(--border)" }} />

          {/* Gift thumbnails — grid mode only */}
          {hasGifts && !isSingleColumn && (
            <div
              className="rounded-xl px-3 py-2.5 border flex items-center gap-2"
              style={{
                backgroundColor: "rgba(200,169,110,0.05)",
                borderColor: "rgba(200,169,110,0.18)",
              }}
            >
              <Gift className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />
              <div className="flex gap-1.5 flex-1 min-w-0">
                {product.giftProducts?.slice(0, 3).map((gc) => (
                  <RenderGiftItems
                    key={gc.gift.id}
                    gift={gc.gift}
                    className="w-8 h-8 flex-shrink-0 relative"
                  />
                ))}
                {product.giftProducts && product.giftProducts.length > 3 && (
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center border text-xs font-bold flex-shrink-0"
                    style={{
                      backgroundColor: "var(--background)",
                      borderColor: "var(--border)",
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-montserrat)",
                    }}
                  >
                    +{product.giftProducts.length - 3}
                  </div>
                )}
              </div>
              <span
                className="text-xs font-semibold flex-shrink-0"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Quà kèm
              </span>
            </div>
          )}

          {/* ── CTA buttons ── */}
          <div className="mt-auto flex gap-2">
            {/* Buy now */}
            <button
              disabled={isOutOfStock}
              onClick={(e) => handleAddToCart(e, true)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-black transition-all duration-150 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                fontFamily: "var(--font-montserrat)",
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                boxShadow: isOutOfStock ? "none" : "0 2px 12px rgba(200,169,110,0.28)",
              }}
              onMouseEnter={(e) => {
                if (!isOutOfStock) {
                  (e.currentTarget as HTMLElement).style.opacity = "0.9";
                  (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 20px rgba(200,169,110,0.42)";
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = "1";
                (e.currentTarget as HTMLElement).style.boxShadow = isOutOfStock ? "none" : "0 2px 12px rgba(200,169,110,0.28)";
              }}
            >
              <Zap className="w-3.5 h-3.5" />
              Mua ngay
            </button>

            {/* Add to cart */}
            <button
              disabled={isOutOfStock}
              onClick={(e) => handleAddToCart(e, false)}
              className="w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-150 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              style={{
                backgroundColor: "rgba(200,169,110,0.08)",
                borderColor: "rgba(200,169,110,0.28)",
                color: "var(--brand-primary)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = "rgba(200,169,110,0.16)";
                el.style.borderColor     = "rgba(200,169,110,0.5)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = "rgba(200,169,110,0.08)";
                el.style.borderColor     = "rgba(200,169,110,0.28)";
              }}
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          </div>

        </div>
      </article>
    </Link>
  );
};